import { err, ok, type Result } from '../result.ts';
import type { ServiceOrderStatus } from '../consultation/service-orders.ts';

// Radiology RIS lite (docs/plans/phase2-radiology.md §5). The order is the Phase 1 imaging
// `service_orders` row; scheduling and the report sit beside it. These functions derive the
// exam's state from those facts and say which report changes are allowed. Nothing is
// deleted: a signed report is amended into a new version.

/** Words, not codes: DICOM modality codes come with PACS in Phase 3 (plan R9). */
export const IMAGING_MODALITIES = [
  'x_ray',
  'ultrasound',
  'ct',
  'mri',
  'mammography',
  'fluoroscopy',
  'other',
] as const;
export type ImagingModality = (typeof IMAGING_MODALITIES)[number];

export const IMAGING_MODALITY_LABELS: Record<ImagingModality, string> = {
  x_ray: 'X-ray',
  ultrasound: 'Ultrasound',
  ct: 'CT',
  mri: 'MRI',
  mammography: 'Mammography',
  fluoroscopy: 'Fluoroscopy',
  other: 'Other imaging',
};

export const IMAGING_REPORT_STATUSES = ['reported', 'verified'] as const;
export type ImagingReportStatus = (typeof IMAGING_REPORT_STATUSES)[number];

export const IMAGING_EXAM_STATES = [
  'requested',
  'scheduled',
  'performed',
  'reported',
  'verified',
  'cancelled',
  'entered_in_error',
] as const;
export type ImagingExamState = (typeof IMAGING_EXAM_STATES)[number];

/** Where an exam is in requested → scheduled → performed → reported → verified. */
export function imagingExamState(facts: {
  orderStatus: ServiceOrderStatus;
  scheduledFor: Date | null;
  reportStatus: ImagingReportStatus | null;
}): ImagingExamState {
  if (facts.orderStatus === 'cancelled' || facts.orderStatus === 'entered_in_error') {
    return facts.orderStatus;
  }
  if (facts.orderStatus === 'requested') {
    return facts.scheduledFor === null ? 'requested' : 'scheduled';
  }
  return facts.reportStatus ?? 'performed';
}

export type ImagingReportAction = 'sign' | 'amend' | 'verify';
export type ImagingReportErrorCode =
  'imaging_report.exists' | 'imaging_report.not_found' | 'imaging_report.not_verifiable';

/**
 * The report's next status. `current` is null before anything is signed. Signing and
 * amending make the report "reported" unless the signer verifies it at the same time
 * (plan R7, R12, TODO(clinical-verify)).
 */
export function imagingReportTransition(
  current: ImagingReportStatus | null,
  action: ImagingReportAction,
  options: { verifyNow: boolean },
): Result<ImagingReportStatus, ImagingReportErrorCode> {
  const signed: ImagingReportStatus = options.verifyNow ? 'verified' : 'reported';
  switch (action) {
    case 'sign':
      return current === null ? ok(signed) : err('imaging_report.exists');
    case 'amend':
      return current === null ? err('imaging_report.not_found') : ok(signed);
    case 'verify':
      if (current === null) return err('imaging_report.not_found');
      return current === 'reported' ? ok('verified') : err('imaging_report.not_verifiable');
  }
}

/** TODO(business): plan R5, the scheduling window. */
export const SCHEDULE_PAST_TOLERANCE_MINUTES = 15;
export const SCHEDULE_MAX_DAYS_AHEAD = 90;

export type ScheduleTimeProblem = 'in_past' | 'too_far_ahead';

export function scheduleTimeProblem(scheduledFor: Date, now: Date): ScheduleTimeProblem | null {
  const earliest = now.getTime() - SCHEDULE_PAST_TOLERANCE_MINUTES * 60_000;
  const latest = now.getTime() + SCHEDULE_MAX_DAYS_AHEAD * 24 * 60 * 60_000;
  if (scheduledFor.getTime() < earliest) return 'in_past';
  if (scheduledFor.getTime() > latest) return 'too_far_ahead';
  return null;
}

export const IMAGES_LINK_MAX_LENGTH = 500;

/**
 * The images link placeholder (plan §1.2): a PACS viewer address someone pastes. Only
 * http and https, never credentials in the address. Wodi stores it and never fetches it.
 */
export function isImagesLink(text: string): boolean {
  if (text.length > IMAGES_LINK_MAX_LENGTH) return false;
  // Scheme, then an authority with a host and no user or password (no "@"), then an
  // optional path, query or fragment; no spaces or control characters anywhere.
  return /^https?:\/\/[^\s/?#@]+(?:[/?#]\S*)?$/i.test(text);
}
